/**
 * The Visitors Desk user manual: /visitors/desk/help.
 *
 * Static help inside the Visitors Desk shell, so editor and writer accounts (who reach only Articles and
 * Issues) can read it too. Linked from the foot of the left column and from "How this works" at the end
 * of every tab bar, which opens the section for that tab (manual.ts TAB_HELP). Keep the button, tab,
 * column and chip names here in step with the screens when those change: DeskFrame, DeskToday,
 * DeskSources, DeskSearch, DeskFlows, DeskArticles, DeskArticle, DeskIssues, DeskAudience, DeskTrends,
 * DeskRevenue, DeskBotPanel, DeskSettings, agent/* and lib/desk*.ts hold the words this page quotes.
 * Say only what a screen does.
 */
import { ContentsRail, H3, K, Note, Qa, Section, Table } from '../../../components/manual/parts'
import { link, p, ul, useHashScroll } from '../../../components/manual/styles'
import { CONTENTS } from './manual'

const b = 'text-paper'

export function ManualPage() {
  // Arriving from a tab's help link (/visitors/desk/help#flows): the router does not scroll to it.
  useHashScroll()

  return (
    <div className="mx-auto grid max-w-[1180px] gap-10 px-0 pb-20 pt-4 font-ui text-paper md:px-6 lg:grid-cols-[200px_minmax(0,1fr)]">
      <ContentsRail items={CONTENTS} />

      <article className="flex min-w-0 flex-col gap-10">
        <header className="flex flex-col gap-3">
          <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-amber-glow">User manual</span>
          <h1 className="m-0 font-serif text-[44px] font-medium leading-[1.05] tracking-tight">Visitors Desk</h1>
          <p className={p}>
            The Visitors Desk shows who reads each title's website, where they come from, what they read and whether
            they come back. It is built around one idea from the best subscription publishers: <strong className={b}>habit
            matters more than volume</strong>. Someone who visits five days a month is far more likely to join, and to stay,
            than someone who reads twenty pages once. So the desk leads with the <a href="#words" className={link}>reader
            ladder</a>, and every other screen helps explain why people move up or down it.
          </p>
          <Note title="The one rule">
            Every number on the desk is people, not robots. Bot and unconfirmed visits are removed everywhere, and the chip at the
            top right always says how many were hidden. Nothing on the desk sends, publishes or changes anything: it only reads.
          </Note>
        </header>

        <Section id="start" title="Start here">
          <H3>Getting there</H3>
          <p className={p}>
            From the AAMCirc console, click <K>Visitors Desk</K> in the left-hand rail. When the analyst is switched on it opens
            on <K>Summary</K> for All titles; otherwise on <K>Today</K>. Editors and writers get there from the Editorial Desk:
            under <strong className={b}>Readers</strong> in its left column, <K>Article &amp; issue stats · &lt;title&gt;</K>, or{' '}
            <K>Open in the Visitors Desk →</K> on a piece's Readers panel.
          </p>

          <H3>Finding your way</H3>
          <p className={p}>The left-hand column has, from the top:</p>
          <ul className={ul}>
            <li><K>Summary</K>: the analyst's write-up and recommendations (admins, when the analyst is on). See <a href="#summary" className={link}>Summary and the analyst</a>.</li>
            <li>One link per title: <K>All titles</K>, then Flight Journal, Model Airplane News, RC Car Action and Diecast X. Switching title keeps the tab you are on.</li>
            <li><K>Desk settings</K> (admins).</li>
            <li>At the foot: who you are signed in as, the screen style (Light, Dark or Match my computer, shared with the Editorial Desk), this <K>User manual</K>, and the way back to the console or the Editorial Desk.</li>
          </ul>
          <p className={p}>
            Across the top of each title are the tabs: <K>Today</K>, <K>Sources</K>, <K>Search</K>, <K>Flows</K>, <K>Articles</K>,{' '}
            <K>Issues</K>, <K>Audience</K>, <K>Trends</K> and <K>Revenue</K>. At the end of the tab bar, <K>How this works</K> opens
            the part of this manual for the tab you are on.
          </p>

          <H3>The pickers at the top</H3>
          <Table
            head={['Picker', 'What it does']}
            rows={[
              ['Period', <><K>Last 7 days</K>, <K>Last 28 days</K> (the default), <K>Last 90 days</K>, <K>Month to date</K>, <K>Last 12 months</K> or <K>Custom</K> (pick <K>From</K> and <K>To</K>, up to 400 days). Periods end yesterday, the last finished day, Eastern time. <K>Today (live)</K> is offered only on Sources, Flows and Articles.</>],
              ['Compare', <><K>Previous period + last year</K> (the default), <K>Previous period only</K> or <K>Last year only</K>. Previous is the same length just before; for Month to date it is the same days of last month. Last year is moved back 364 days so the weekdays line up.</>],
              ['Who', <><K>Everyone</K>, <K>Non-members</K>, <K>Lapsed</K> or <K>Members</K>. See <a href="#words" className={link}>the four groups</a>. A screen that can't be split says so in a grey note, for example <em>Everyone: Google can't tell members apart.</em></>],
              ['Humans only chip', <>Reads <K>Humans only · N bot visits hidden</K>. Click it for the bot panel (admins): the share of visits hidden, the top reasons, and a day-by-day check of our count against Google Analytics, with drift over 25% flagged. <K>See the raw visits (N) →</K> opens every visit, bots included.</>],
            ]}
          />
          <p className={p}>
            Your choices are kept in the page address, so you can bookmark a view or paste the link to a colleague and they will
            see the same title, period, group and filters.
          </p>

          <H3>Lines under the pickers</H3>
          <ul className={ul}>
            <li><em>Data through … · last event … · N events waiting to be grouped</em>: how fresh the figures are.</li>
            <li><em>Rollups through …</em>: the period includes days that aren't finished or added up yet.</li>
            <li><em>Includes … Not yet: …</em> (All titles): which titles are in the totals. A title joins once its new website is live.</li>
            <li><em>Groups before … are reconstructed from membership dates and may be approximate.</em>: shown when you pick a group. We have kept a daily record of who is a member only since the desk started; earlier days are worked out from membership dates.</li>
          </ul>

          <H3>Filters</H3>
          <p className={p}>
            Some clicks add a filter, shown as a pill such as <K>Referrer: rcgroups.com ×</K> under the tabs. Click the × to
            remove it. A tab that can't use a filter keeps the pill but marks it grey, for example <em>Not filtered by
            Channel</em>, so you always know what you are looking at. Admins also get <K>See the visits →</K>, which opens the
            individual visits behind the filters.
          </p>

          <H3>When a tab is empty</H3>
          <Table
            head={['Message', 'Meaning']}
            rows={[
              ['Starts when the new site goes live', 'We only measure our own reader websites. A title still on WordPress has no figures here yet (its Search tab works from Google data).'],
              ['Collecting — the first daily summary is built overnight', 'The site is live but the first night\'s figures aren\'t built yet.'],
              ['No Search Console data yet', 'Google hasn\'t sent us search figures for this title.'],
              ['Couldn’t check availability', 'The desk couldn\'t reach the server. Reload the page.'],
            ]}
          />
        </Section>

        <Section id="words" title="The words it uses">
          <H3>The reader ladder</H3>
          <p className={p}>
            Everyone who was active in the last 28 days sits on the highest rung they reached. Active means a web visit, an
            issue opened or paged in the viewer, a PDF download, or a click in one of our emails. Email opens don't count:
            Apple Mail opens every email automatically, so opens say little.
          </p>
          <Table
            head={['Rung', 'Who']}
            rows={[
              ['Fly-by', '1 visit day in the 28.'],
              ['Occasional', '2–4 visit days.'],
              ['Regular', '5 or more visit days. The rung that matters most: regulars are the people most likely to join.'],
              ['Known', 'Not a member, but we know their email: a free reader account, a confirmed newsletter sign-up, or a click in one of our emails.'],
              ['Member', 'A paid member of this title, by any payment method.'],
              ['Engaged member', 'A member active on 4 or more days in the 28 (web, issue or email click). Shown as the active part of Members.'],
            ]}
          />
          <p className={p}>
            Arrows show movement against each person's rung 28 days earlier. Members and known people who did nothing in the 28
            days aren't rungs; they appear in a grey line under the ladder, <em>Not seen in 28 days: N members, N known</em>.
          </p>

          <H3>The four groups</H3>
          <p className={p}>Groups are per title: a Flight Journal member reading Model Airplane News is a non-member there.</p>
          <Table
            head={['Group', 'Who', 'Why it matters']}
            rows={[
              ['Everyone', 'Every human visitor.', 'The overall picture.'],
              ['Non-members', 'Never a member of this title, or not for over 18 months. Includes anonymous visitors and known people.', 'People to convert.'],
              ['Lapsed', 'A membership of this title ended in the last 18 months (548 days). Split into ended within 90 days, and 91 days to 18 months.', 'People to win back.'],
              ['Members', 'Active members (Active, Bad Address or On Hold).', 'People to keep.'],
            ]}
          />
          <p className={p}>
            A visit belongs to the group the person was in on that day, so someone who lapsed last week counts as a member
            before that. Over a period, each person is counted once, in the group of their last visit, so the three groups add
            up to Everyone.
          </p>

          <H3>Other terms</H3>
          <Table
            head={['Term', 'Meaning']}
            rows={[
              ['People', 'Different visitors. A visitor signed in on two devices counts once; an anonymous visitor on two devices counts twice. In Sources and Articles a person is counted once per day, so someone who comes back on 3 days counts 3.'],
              ['Real read', 'On the article for at least 15 seconds and scrolled past a quarter of it.'],
              ['Read to the end', 'Scrolled to the bottom of the article.'],
              ['Read another page', 'Went on to a different page in the same visit.'],
              ['Arrived here first', 'Visits that started on this page.'],
              ['New member', 'A brand-new member, or a win-back whose last membership ended more than 90 days before. Renewals, auto-renew charges, conversions from SimpleCirc, cheque renewals and comps don\'t count. Gift and BOGO purchases count, credited to the buyer.'],
              ['Members gained', 'New members credited to an article or source: anyone who joined within 30 days of a visit that included it. The credit is shared evenly across every article they read, so it can be a fraction such as 0.3.'],
              ['Return / win-back', 'A lapsed person who rejoined: within 90 days of lapsing is a return, after that a win-back.'],
            ]}
          />

          <H3>Honest small numbers</H3>
          <ul className={ul}>
            <li><strong className={b}>Minimum base 30.</strong> A percentage or rate built on fewer than 30 shows <em>—</em>, and the count stays. A change on a small base shows as a number (<em>+5</em>) instead of a percentage.</li>
            <li><strong className={b}>Changes</strong> read <em>▲ 12%</em> for counts and <em>▲ 3 pts</em> for rates.</li>
            <li><strong className={b}>Normal range.</strong> A value is judged against the same kind of period before it: one day against the same weekday in each of the previous 8 weeks, a longer period against the 8 periods of the same length before it (Month to date against the first days of the previous 8 months). The verdict is <K>Normal</K>, <K>Above</K> or <K>Below</K> (on Today, <em>Inside normal range</em> and so on). Normal means it is within what the title usually swings, so it isn't worth reacting to. With fewer than 4 earlier periods it says <K>Collecting</K>.</li>
            <li><strong className={b}>Last year</strong> is shown for context but never decides a verdict. Until we have a year of our own figures, some tiles show Google Analytics history marked <em>GA4</em>. It is counted differently from ours, so it sits beside our numbers and is never mixed into them.</li>
            <li><strong className={b}>Unfinished periods</strong> are drawn dotted and never compared with a finished one.</li>
          </ul>
        </Section>

        <Section id="summary" title="Summary and the analyst">
          <p className={p}>
            <K>Summary</K> is a short written report by an AI analyst, for one title or All titles. It is for admins, and only
            when the analyst is switched on for the server.
          </p>
          <H3>What it writes</H3>
          <ul className={ul}>
            <li><strong className={b}>The short version</strong>: four lines, one per group (Everyone, Non-members, Lapsed members, Members), each <em>▲ Growing</em>, <em>▬ Flat</em> or <em>▼ Shrinking</em>, with one sentence and a link to the figure. It says Flat unless the change is outside the normal range. The measures are regular readers; regular-to-member conversion; returns and win-backs; members active and going quiet.</li>
            <li><strong className={b}>Detail</strong>: one card per group (click − or + to fold it) with the evidence.</li>
            <li><strong className={b}>What to do</strong>: up to 6 recommendations, each marked <K>Convert</K>, <K>Win back</K>, <K>Retain</K> or <K>Overall</K>, with the group and <em>Confidence: high, medium or low</em>.</li>
          </ul>
          <p className={p}>
            The foot of the short version says when it was written, by which model, and what it cost. Every number the analyst
            writes is checked against the figures it looked up; if one doesn't match, it tries once more and otherwise refuses to
            publish. A title whose new site isn't live gets <em>Starts when the new site goes live</em> instead.
          </p>

          <H3>When it runs</H3>
          <ul className={ul}>
            <li>Every morning, for the last 7 days against the 7 before, once <K>Analyst writes a summary every morning</K> is on in Desk settings. Monday's is a weekly edition that adds the last 28 days.</li>
            <li><K>Refresh</K> writes a new one now (it shows <em>Writing…</em> and the page updates by itself). It works even when the morning run is off.</li>
            <li><K>Past summaries</K> reopens earlier ones by date.</li>
          </ul>

          <H3>Handing a recommendation on</H3>
          <p className={p}>A recommendation can carry one button. Nothing is sent or published from here: each one only makes a draft for someone to approve.</p>
          <Table
            head={['Button', 'What happens']}
            rows={[
              [<K>Send to Campaign Desk</K>, 'Creates a draft brief on the Campaign Desk with the audience and the idea. The Campaign Desk strategist builds the campaign, and nothing is emailed until it is approved there. The button becomes Sent to Campaign Desk ↗, a link to the brief. Shown only for titles where the Campaign Desk runs.'],
              [<K>Send to Editorial Desk</K>, 'Marks the recommendation as handed on and becomes Sent to Editorial Desk ↗, a link to the title\'s Start a piece page with the idea filled in. Nothing is created until an editor starts the piece.'],
              [<K>Discuss</K>, 'Opens a conversation with the analyst about that recommendation.'],
            ]}
          />

          <H3>Asking the analyst</H3>
          <p className={p}>
            Type a question in <strong className={b}>Ask the analyst</strong>, for example "Why did members active drop this
            week?", and press <K>Send</K> (or Ctrl+Enter). It looks the answer up in the same figures as the desk screens and
            links to them. Conversations are saved under <strong className={b}>Conversations</strong> for every admin to reopen;{' '}
            <K>New conversation</K> starts a fresh one.
          </p>
          <Note title="Counts, never names">
            The analyst sees counts only. It can't name a member, list people or change anything. For people, it points you to
            the Subscriber Desk.
          </Note>
        </Section>

        <Section id="today" title="Today">
          <p className={p}>The daily look: what moved, and what is happening right now. Read it top to bottom in a minute.</p>
          <H3>Reader ladder</H3>
          <p className={p}>
            Six cards, one per rung, with how many moved up and down. The ladder always covers the 28 days to the end of the
            period, whatever period you pick. Under it, <strong className={b}>Step-up rates</strong> show how many moved up a
            rung: <em>Fly-by → Occasional</em>, <em>Occasional → Regular</em>, <em>Regular → Member</em> (new members against the
            regulars 28 days ago: the number to grow) and <em>Members active</em>.
          </p>
          <H3>What changed since yesterday</H3>
          <p className={p}>
            Up to 6 items, each with a link to the screen that explains it: alerts that fired, figures outside the normal range,
            a new site sending people, an article taking off, how the current issue compares with the last one, a search term
            close to page one, and the biggest mover. <em>Nothing unusual since yesterday.</em> means just that.
          </p>
          <H3>Right now</H3>
          <p className={p}>
            Confirmed humans in the last 5 minutes, updated every 30 seconds: <em>people reading</em>, <em>members in an
            issue</em> and <em>in checkout</em>, and what they are reading with where they came from. The grey{' '}
            <em>unconfirmed</em> figure is visits not yet proved human; it isn't added in. Click a page to see those visits.
          </p>
          <H3>The tiles</H3>
          <p className={p}>
            For Everyone: <K>People</K>, <K>Regular readers</K>, <K>Google search clicks</K> (Google is 2–3 days behind) and{' '}
            <K>Members active</K>, each against the previous period and last year, with its normal-range verdict. Pick a group
            under <K>Who</K> and the tiles change to that group's job:
          </p>
          <Table
            head={['Group', 'Tiles']}
            rows={[
              ['Non-members', 'People, Regular readers, Regular → member, Paywall views, Checkout starts, New members.'],
              ['Lapsed', 'People, Return visits, Lapsed ≤ 90 days, Lapsed 91 days–18 months, Returns, Win-backs.'],
              ['Members', 'People, Members active, Engaged members, Not seen in 28 days, Issue pickup, Going quiet, ending in 30 days (membership ends within 30 days, active before, but not in the last 14 days).'],
            ]}
          />
          <H3>Biggest movers and All titles</H3>
          <p className={p}>
            <strong className={b}>Biggest movers</strong> lists the sources, articles and search terms that rose or fell most
            against the previous period; rows under 30 in the earlier period are left out. The <strong className={b}>All
            titles</strong> table puts every live title side by side, with a small picture of each one's ladder.
          </p>
        </Section>

        <Section id="sources" title="Sources">
          <p className={p}>Where people came from, and what each source turned into.</p>
          <H3>Where people came from</H3>
          <p className={p}>
            Channels (Search, Forums &amp; clubs, Social, Email &amp; newsletters, AI assistants, Other sites, Paid, Internal and
            three kinds of Direct), each opening into its sources. The last three columns, <K>Became regular</K>,{' '}
            <K>Signed up (confirmed)</K> and <K>Members gained</K>, show which sources bring readers who stay.
          </p>
          <p className={p}>
            <em>Direct, deep link</em> is a visit with no referrer that landed on an article. These are nearly always links shared
            by text, email or messenger: word of mouth, not typed-in visits.
          </p>
          <p className={p}>
            The change column reads <K>vs prev</K>, or <K>vs last year</K> when Compare is set to last year only. Click a row
            to pick it out (<K>See the visits →</K> opens its visits); clicking a site row also opens <strong className={b}>Exactly which pages sent them</strong>:
            the forum thread or page that linked to us, where people landed, and how many became regular or joined. Google,
            Facebook and apps send only their site name, and those rows say <em>(no page sent)</em>.
          </p>
          <H3>New sources</H3>
          <p className={p}>
            Sites that hadn't sent anyone in the previous 90 days. Someone is talking about us there: join the thread, thank the
            club, or pitch the forum. While we have less than 90 days of history, the panel says it is still collecting.
          </p>
          <H3>Email &amp; newsletters</H3>
          <p className={p}>
            Each send with how many opened, the visits it brought, <K>vs usual</K> (against that title's last 8 sends of the same
            kind) and <K>Members from it</K> (people who clicked through from the send and paid). Pick a finished period; it doesn't work
            on Today (live).
          </p>
          <H3>Social &amp; AI assistants</H3>
          <p className={p}>Each network (Facebook, YouTube, Reddit, ChatGPT, Perplexity and so on) with its top post or page.</p>
        </Section>

        <Section id="search" title="Search">
          <p className={p}>
            What people searched in Google to find us. These figures come from Google Search Console, which is 2–3 days behind
            and can't tell members apart, so this tab is always Everyone.
          </p>
          <H3>Tiles</H3>
          <p className={p}>
            <K>Google clicks</K>, <K>Times shown in Google</K>, <K>Average position</K> (lower is better, so ▲ means we moved up)
            and <K>Discover clicks</K>.
          </p>
          <H3>Search terms people used to find us</H3>
          <p className={p}>Five views, each with a <strong className={b}>Suggested move</strong> on every row:</p>
          <Table
            head={['View', 'Shows', 'Usual move']}
            rows={[
              ['Close to page one', 'Terms where we rank 8–20, most shown first.', 'Link to the page from related articles, or refresh and expand it.'],
              ['Ranked, not clicked', 'Top-5 positions whose click rate is below what this title usually gets at that rank.', 'Rewrite the Google title and description.'],
              ['Rising / falling', 'Clicks against the previous period of the same length.', 'Feature risers; refresh pages that lost ranking or are 2+ years old.'],
              ['Brand vs topic', 'Searches for the title\'s name against searches for subjects. Brand words are set in Desk settings.', '—'],
              ['Discover & News', 'Pages picked up by Google Discover or Google News.', 'Share it; follow up with a related story.'],
            ]}
          />
          <p className={p}>
            Google hides rare search terms, so the list covers only part of the clicks; the line under it says how much. The tile
            totals are complete.
          </p>
          <H3>Two of our pages competing for one search</H3>
          <p className={p}>Searches where Google keeps swapping between two of our pages. Pick one page: merge the other or link it to the stronger one.</p>
          <H3>Searches on our own site</H3>
          <p className={p}>
            What readers typed in our own search boxes (topics, archive, editions, cars) and what they clicked next. Tick{' '}
            <K>Zero results only</K> for searches that found nothing: things readers want that we don't have or can't find.
            They are story ideas and an archive-tagging list.
          </p>
        </Section>

        <Section id="flows" title="Flows">
          <p className={p}>How people move through the site: came from → landed on → went next → ended.</p>
          <ul className={ul}>
            <li><strong className={b}>How traffic flows</strong>: the top 5 at each step, the rest as Other. Click a block to follow only that stream; click it again to let go. Endings are <em>Left</em>, <em>Read 3+ pages</em>, <em>Signed up (confirmed)</em>, <em>Became a member</em> and <em>Started checkout, didn’t finish</em>. Under it, the 10 most common complete paths.</li>
            <li><strong className={b}>Landing pages</strong>: what happens after people arrive. <K>Read a 2nd page</K> turns orange when a page has 100+ arrivals and under 20% go on: fix it with better related links or a member teaser at the end.</li>
            <li><strong className={b}>Where people left</strong>: the last page seen, with a usual reason where the desk can tell (abandoned at checkout, looked at the price, couldn't sign in, paywall stop, one-visit readers from Search or Social).</li>
            <li><strong className={b}>Links clicked out of our site</strong>: which shops, makers, clubs and events readers go to. Useful for advertisers and partners. Counted overnight.</li>
          </ul>
          <p className={p}>Following a stream changes the flow diagram only; the three tables below it stay for the whole period and say so.</p>
        </Section>

        <Section id="articles" title="Articles">
          <p className={p}>How each article performs. Editors and writers see this tab for their own titles.</p>
          <H3>The table</H3>
          <p className={p}>
            Narrow it with <K>Section</K>, <K>Published</K> (<K>Any time</K>, <K>This period</K>, <K>Over a year ago</K>) and{' '}
            <K>Access</K> (free, members or both). Then choose a ranking:
          </p>
          <Table
            head={['Ranking', 'Puts first']}
            rows={[
              ['Most read', 'The most people.'],
              ['Brings people in', 'The most arrivals from outside: search, social, forums, shared links.'],
              ['Keeps members', 'The most member real reads and reads to the end. These are the reason people renew.'],
              ['Turns readers into members', 'The most members gained for its readers. Small numbers are ranked cautiously, so 1 join from 3 readers doesn\'t top the list.'],
            ]}
          />
          <p className={p}>
            Columns: <K>People</K>, <K>Real reads</K>, <K>Read to the end</K>, <K>Read another page</K>, <K>Arrived here
            first</K>, <K>Hit paywall</K>, <K>Members gained</K> and <K>Who read it</K> (a bar split into anonymous, known and
            members; point at it for the counts). On Today (live), members gained shows a dash: it is credited overnight.
          </p>
          <H3>Sections &amp; topics, and old articles</H3>
          <p className={p}>
            <strong className={b}>Sections &amp; topics · what to write more of</strong> ranks sections (or tags) by people per
            article. <strong className={b}>Old articles still pulling readers</strong> lists pieces over a year old that still
            draw people, and where those readers come from: keep them fresh and link them to new pieces. Both cover the whole
            title and ignore the table's filters.
          </p>
          <H3>One article</H3>
          <p className={p}>
            Click a title for its page: the same figures for the period, a day-by-day line of people and real reads, where
            readers came from, the Google search terms that found it, where readers went next, and how many members joined with
            this as the last article they read. Admins with member-history access also see <strong className={b}>Named
            readers</strong>; nobody else does.
          </p>
        </Section>

        <Section id="issues" title="Issues">
          <p className={p}>
            How members read each digital issue, in the web viewer and as PDF downloads. Issues are members only, so the{' '}
            <K>Who</K> switch has nothing to split here. Editors and writers see this tab for their own titles.
          </p>
          <H3>One issue</H3>
          <p className={p}>
            Pick it from <K>Issue</K>; the current one is marked. The figures are as of the last finished day, and every share is
            of today's members. On All titles, picking an issue moves you to its title.
          </p>
          <ul className={ul}>
            <li><strong className={b}>Tiles</strong>: <K>Members who opened it</K>, <K>PDF downloads</K>, <K>Median pages read</K> and <K>Read on a phone</K>, each against the previous issue at the same number of days since release.</li>
            <li><strong className={b}>Pickup</strong>: the share of members who have opened it, day by day since release, beside the previous 3 issues, with the new-issue email marked. A jump after the email shows the announcement working; a flat start means the email or the cover didn't land.</li>
            <li><strong className={b}>How far into the issue readers get</strong>: the share of openers reaching each tenth of the issue (needs 30 openers).</li>
            <li><strong className={b}>Most-read spreads</strong>: which pages readers stop on, and for how long. <em>What's there</em> comes from the Inside the issue picks.</li>
          </ul>
          <H3>All issues</H3>
          <p className={p}>
            The last 24 issues with <K>Members reached</K>, <K>Opened in viewer</K>, <K>PDF downloads</K>, <K>Median pages
            read</K>, <K>Days to reach 15%</K> of members, and <K>Opens this period</K> (the only column that follows the Period
            picker). The line under it says what share of this period's opens were back issues, opened more than 62 days after
            release: archive value members are using.
          </p>
        </Section>

        <Section id="audience" title="Audience">
          <p className={p}>Who the readers are and how they behave.</p>
          <ul className={ul}>
            <li><strong className={b}>New vs returning people, by week</strong>: the last 8 weeks, with how many new people came back within 7 days and within 4 weeks.</li>
            <li><strong className={b}>How often people visit</strong>: people by days visited in the last 28 (1, 2, 3–4, 5–9, 10–19, 20+). The raw material of the ladder.</li>
            <li><strong className={b}>Where they are</strong>: countries (as codes such as US), with US states and Canadian provinces underneath.</li>
            <li><strong className={b}>Device</strong>: people per device, and the share of their visits that were real reads.</li>
            <li><strong className={b}>Who they are to us</strong>: anonymous, known, lapsed members (within 90 days and older) and members, with pages per person.</li>
            <li><strong className={b}>When they read</strong>: a weekday by time-of-day grid in Eastern time. Darker is more people. Use it to choose when to send the newsletter and publish.</li>
          </ul>
          <p className={p}>The weekly and 28-day panels run to the end of the period you pick; their length doesn't change.</p>
        </Section>

        <Section id="trends" title="Trends">
          <p className={p}>Is the title growing or shrinking, beyond the day-to-day noise?</p>
          <ul className={ul}>
            <li><strong className={b}>The line</strong>: pick a <K>Metric</K> (people, regular readers, known, members gained, members active, Google search clicks, social, email visits, issue opens, bot share). It always shows the last 365 days: daily values, a 28-day average, the shaded normal range, last year dashed, and flags on days an alert fired. Before our own tracking started, Google Analytics history is drawn and marked GA4.</li>
            <li><strong className={b}>The scorecard</strong>: each headline measure for the period against the previous period and the same period last year, with its verdict. Only Above or Below deserves attention.</li>
            <li><strong className={b}>Reader ladder, month by month</strong>: the last 12 months. The truest growth measure: is the middle of the ladder getting thicker?</li>
            <li><strong className={b}>Alerts</strong>: each alert rule, whether it is on, and when it last fired. <K>Change alerts in Desk settings →</K> to change them.</li>
          </ul>
        </Section>

        <Section id="revenue" title="Revenue">
          <p className={p}>
            The money side, one title at a time (All titles asks you to pick one). It follows the Period, but not Compare or Who.
          </p>
          <ul className={ul}>
            <li><strong className={b}>Revenue</strong>: new members, net revenue, average order and payment mix.</li>
            <li><strong className={b}>Member engagement</strong>: members active in the last 30 days, 7 days and today, reads, edition opens, issue downloads and sign-ins; members renewing within 30 days who are at risk; and reader retention by month.</li>
            <li><strong className={b}>Revenue funnel</strong>: visits → engaged → considered → checkout started → paid, overall and by channel. Each step opens the visits behind it.</li>
            <li><strong className={b}>Email</strong>: campaigns and the visits they brought.</li>
            <li><strong className={b}>Visitors per day</strong>.</li>
          </ul>
          <p className={p}>Names on the at-risk list and downloads pages need member-history access.</p>
        </Section>

        <Section id="alerts" title="Alerts and the weekly digest">
          <H3>Alerts</H3>
          <p className={p}>
            An alert appears in <em>What changed since yesterday</em> on Today, as a flag on the Trends line, and by email to
            admins. Emails are batched: at most one per title per hour. The rules, each of which can be switched off or retuned in
            Desk settings:
          </p>
          <Table
            head={['Alert', 'Fires when (default)', 'Checked']}
            rows={[
              ['New site linking to us', 'A site unseen for 90 days sends 10+ people.', 'Hourly and nightly'],
              ['Article taking off', '3× its usual daily readers, at least 50.', 'Hourly and nightly'],
              ['Traffic outside normal', 'People, Google search clicks or email visits outside the usual band for that weekday.', 'Nightly'],
              ['Picked up by Google Discover', 'Any article with 100+ Discover clicks in a day.', 'Nightly'],
              ['Checkout drop', 'New members below normal for 2 days running.', 'Nightly'],
              ['Bot surge', 'Bot share jumps 10+ points: a warning that the figures may be off.', 'Nightly'],
              ['New issue slow to start', 'Day-3 pickup below the last 3 issues. Off unless switched on.', 'Nightly'],
            ]}
          />
          <p className={p}>
            An alert caught during the day is rechecked overnight with the finished figures; its wording is updated, and it isn't
            emailed twice.
          </p>
          <H3>The weekly digest</H3>
          <p className={p}>
            When switched on, each title's digest goes on Monday morning (from 7am Eastern) for the last full Monday-to-Sunday
            week. It has three headline numbers (people, regular readers, new members) against the previous 8 weeks, then up to
            3 <strong className={b}>wins</strong>, 3 <strong className={b}>worries</strong> and 3 <strong className={b}>opportunities</strong>,
            and a link to the desk. The wording is fixed text, not AI. If the week's figures aren't finished by noon, that
            title's digest is skipped for the week.
          </p>
        </Section>

        <Section id="settings" title="Desk settings">
          <p className={p}>Admins only. Each panel saves on its own.</p>
          <Table
            head={['Panel', 'What it sets']}
            rows={[
              ['Forum, shop and club sites', <>Sites and what kind they are (<K>Forum</K>, <K>Shop</K>, <K>Club / museum</K>, <K>Event</K>, <K>Our own channel</K>, <K>Other</K>). A forum counts under Forums &amp; clubs on Sources; the others label outbound links on Flows. Type a site such as rcgroups.com and click <K>Add</K>; changing a kind saves at once; <K>Remove</K> asks you to <K>Confirm remove</K>. Changes apply to visits from now on and take up to 5 minutes to show.</>],
              ['Brand search terms', <>Per title, the words that make a Google search a brand search on the Search tab, one per line or separated by commas. Click <K>Save</K>.</>],
              ['Alerts', <><K>Email alerts</K> switches alert email on or off (they still show on Today and Trends). Each rule has its own switch, numbers and <K>Save</K>.</>],
              ['Who gets alerts', <>Per title, which admins get alert email. Alerts cover revenue and checkout, so only admins can. With nobody ticked, every admin gets them; <K>Use the default (every admin)</K> goes back to that.</>],
              ['Weekly digest', <><K>Send the weekly digest (Mondays 7am Eastern)</K>, and per title who gets it: admins, plus editors and writers for their own titles. <K>Preview</K> shows this week's for any title, and <K>Send a test to me</K> sends it to your own address only.</>],
              ['Analyst', <><K>Analyst writes a summary every morning</K>. Shown only when the analyst is switched on for the server.</>],
            ]}
          />
          <p className={p}>
            If the panel says <em>Desk email is switched off on this server</em>, no alert or digest email goes out from this
            server at all; ask whoever runs the server.
          </p>
        </Section>

        <Section id="access" title="Who sees what">
          <Table
            head={['Account', 'What it reaches']}
            rows={[
              ['Admin', 'Everything: every title, every tab, Summary and the analyst, the bot panel and Desk settings.'],
              ['Editor or writer', <>Only <K>Articles</K> and <K>Issues</K>, only for the titles they hold, as counts, never names. No All titles, Summary, settings or bot panel; the chip reads <em>Humans only · bots removed</em>. The foot of the column goes back to the Editorial Desk.</>],
              ['Other accounts', 'No access. Customer Support, Subscriber Desk and Accounting accounts are sent back to their own desk.'],
            ]}
          />
          <p className={p}>
            Named people (named readers of an article, the at-risk list, downloaders) need member-history access: an admin ticks{' '}
            <K>Can view member reading history</K> on the account under <K>Users</K>, and the server switch for it must be on.
            Editors and writers never see names.
          </p>
        </Section>

        <Section id="faq" title="Questions and problems">
          <div className="flex flex-col gap-3">
            <Qa q="Our numbers are far lower than Google Analytics.">
              Google Analytics counts many robots as people; about seven in ten of the old site's visits were bots. The desk counts
              only visits that behaved like a person. The bot panel compares the two day by day. A separate gap on Model Airplane
              News: readers who land on old WordPress pages the new site doesn't serve aren't seen by our tracker at all.
            </Qa>
            <Qa q="A tab says Starts when the new site goes live.">
              That title's reader site is still on WordPress, so there is nothing of ours to measure yet. Search works from Google
              data before go-live.
            </Qa>
            <Qa q="Why is today missing, or yesterday's search figures low?">
              Periods end with the last finished day. For today, use Right now on the Today tab, or Today (live) on Sources, Flows and Articles. Google
              search figures arrive 2–3 days late.
            </Qa>
            <Qa q="A percentage shows a dash.">
              The base is under 30, too small for a percentage to mean anything. The count is still there.
            </Qa>
            <Qa q="I picked Members and some panels didn't change.">
              Those panels can't be split by group yet (or, for Google data, can't be at all). They show a grey note saying they are
              Everyone.
            </Qa>
            <Qa q="Members gained shows 0.3.">
              A new member's credit is shared across every article they read in the 30 days before joining, so an article often
              gets a fraction.
            </Qa>
            <Qa q="Who are the members going quiet?">
              The desk and the analyst give counts only. Find the people on the Subscriber Desk, or open a member's record from the
              Revenue tab's at-risk list if you have member-history access.
            </Qa>
            <Qa q="The summary failed.">
              The page shows why and keeps the last good summary. If a number couldn't be checked or the analyst ran out of time,
              click <K>Try again</K>.
            </Qa>
          </div>
        </Section>
      </article>
    </div>
  )
}
